import type { CosmosClient } from "@/services/cosmos";
import {
  type CheckoutSession,
  PaymentMethod,
  type StoredCard,
  type AppContext,
} from "@/types";
import { newLocalStorageStore } from "@/lib/store";
import * as lib3ds from "@/lib/3ds";
import { logInfo } from "@/services/logging/logger";
import {
  emitPaymentSubmissionTracking,
  emitApplePayTracking,
} from "@/shared/tracking/analytics";
import * as applePay from "@/lib/apple-pay";
import { browserAgent } from "@/shared/telemetry/newrelic";
import type { PaymentSubmissionOptions } from "../types";

async function clear3dsState(
  client: CosmosClient,
  sessionId: string,
): Promise<void> {
  const store = newLocalStorageStore(window, sessionId);
  await lib3ds.clear3dsState(store);
  lib3ds.clearSessionFlags(client.config().checkoutId);
  logInfo("payment-submission", "Cleared 3DS state for payment submission", {
    sessionId,
  });
}

export async function payWithExistingCard(
  client: CosmosClient,
  session: CheckoutSession,
  cardId: string,
  options: PaymentSubmissionOptions = {},
  cvv?: string,
  fingerprint?: string,
  card?: StoredCard,
): Promise<CheckoutSession> {
  await clear3dsState(client, session.id);

  logInfo(
    "payment-submission",
    "POST request triggered to select-payment-method",
    { paymentMethod: PaymentMethod.CREDIT_CARD, hasCardId: !!cardId },
  );

  const newSession = await client.selectPaymentMethod(
    PaymentMethod.CREDIT_CARD,
    {
      cardId,
      cvv,
      termsAccepted: options.termsAccepted,
      documentsSigned: options.documentsSigned,
      shouldUseWalletBalance: options.shouldUseWalletBalance,
      fingerprint,
      ...(card !== undefined && {
        cardBin: card.bin,
        cardLast4: card.last4,
        cardExpiryMonth: String(card.expiryMonth),
        cardExpiryYear: String(card.expiryYear),
      }),
    },
  );

  logInfo(
    "payment-submission",
    "POST response received from select-payment-method",
    {
      sessionId: newSession.id,
      status: newSession.status,
      paymentMethod: PaymentMethod.CREDIT_CARD,
      hasCardId: !!cardId,
      hasCvv: !!cvv,
    },
  );

  emitPaymentSubmissionTracking("payment_submission_completed", session, {
    payment_method_type: "card",
    card_id: cardId,
    wallet_selected: options.shouldUseWalletBalance ?? false,
  });

  browserAgent.addPageAction("PAYMENT_SUBMISSION_METHOD", {
    payment_method_used: "card",
    wallet_selected: options.shouldUseWalletBalance ?? false,
  });

  return newSession;
}

export async function payWithApple(
  client: CosmosClient,
  ctx: AppContext,
  windowRef: Window,
  isWalletSelected: boolean,
  options: PaymentSubmissionOptions = {},
  amountToPay: number,
): Promise<CheckoutSession> {
  await clear3dsState(client, ctx.session.id);

  emitApplePayTracking("apple_pay_initiated", ctx.session);

  if (ctx.session.shouldShowIvr) {
    logInfo(
      "payment-submission",
      "Apple Pay: shouldShowIvr is true, submitting with null token",
    );

    const newSession = await client.selectPaymentMethod(
      PaymentMethod.APPLE_PAY,
      {
        token: null,
        termsAccepted: options.termsAccepted,
        documentsSigned: options.documentsSigned,
        shouldUseWalletBalance: options.shouldUseWalletBalance,
      },
    );

    logInfo(
      "payment-submission",
      "POST response received from select-payment-method",
      {
        sessionId: newSession.id,
        status: newSession.status,
        paymentMethod: PaymentMethod.APPLE_PAY,
        shouldShowIvr: true,
      },
    );

    return newSession;
  }

  const {
    type: result,
    token,
    session: applePaySession,
  } = await applePay.pay(
    windowRef,
    ctx,
    amountToPay,
    (validationUrl: string) =>
      client.validateAppleSession(
        ctx.configuration,
        validationUrl,
        ctx.session.order.currency,
        ctx.configuration.ckoHubVersion,
        ctx.session.order.countryCode,
      ),
  );

  if (result === applePay.ApplePayResultType.CANCELED) {
    emitApplePayTracking("apple_pay_canceled", ctx.session);
    emitPaymentSubmissionTracking("payment_submission_failed", ctx.session, {
      payment_method_type: "apple_pay",
      wallet_selected: isWalletSelected,
      error_message: "User canceled",
    });
    throw new Error("Apple Pay was canceled");
  }

  if (result === applePay.ApplePayResultType.FAILED) {
    emitApplePayTracking("apple_pay_failed", ctx.session);
    emitPaymentSubmissionTracking("payment_submission_failed", ctx.session, {
      payment_method_type: "apple_pay",
      wallet_selected: isWalletSelected,
      error_message: "Apple Pay failed",
    });
    throw new Error("Apple Pay failed");
  }

  if (result === applePay.ApplePayResultType.OK) {
    const updatedSession = await client.selectPaymentMethod(
      PaymentMethod.APPLE_PAY,
      {
        token,
        termsAccepted: options.termsAccepted,
        documentsSigned: options.documentsSigned,
        shouldUseWalletBalance: options.shouldUseWalletBalance,
      },
    );

    emitApplePayTracking("apple_pay_completed", ctx.session);
    emitApplePayTracking("apple_pay_payment_completed", ctx.session);
    emitPaymentSubmissionTracking(
      "payment_submission_completed",
      ctx.session,
      {
        payment_method_type: "apple_pay",
        wallet_selected: isWalletSelected,
      },
    );

    browserAgent.addPageAction("PAYMENT_SUBMISSION_METHOD", {
      payment_method_used: "apple_pay",
      wallet_selected: isWalletSelected,
    });

    applePay.completePayment(windowRef, applePaySession, true);

    return updatedSession;
  }

  throw new Error("Unknown Apple Pay result");
}

export async function confirmPnmOrder(
  client: CosmosClient,
  session: CheckoutSession,
  options: PaymentSubmissionOptions = {},
): Promise<CheckoutSession> {
  await clear3dsState(client, session.id);

  logInfo(
    "payment-submission",
    "POST request triggered to select-payment-method (PNM confirm)",
    { sessionId: session.id },
  );

  const newSession = await client.selectPaymentMethod(
    PaymentMethod.CREDIT_CARD,
    {
      termsAccepted: options.termsAccepted,
      documentsSigned: options.documentsSigned,
      shouldUseWalletBalance: false,
    },
  );

  logInfo(
    "payment-submission",
    "POST response received from select-payment-method (PNM confirm)",
    { sessionId: newSession.id, status: newSession.status },
  );

  emitPaymentSubmissionTracking("payment_submission_completed", session, {
    payment_method_type: "pnm_confirm",
  });

  browserAgent.addPageAction("PAYMENT_SUBMISSION_METHOD", {
    payment_method_used: "pnm_confirm",
  });

  return newSession;
}

export async function payWithWalletOnly(
  client: CosmosClient,
  session: CheckoutSession,
  options: PaymentSubmissionOptions = {},
): Promise<CheckoutSession> {
  await clear3dsState(client, session.id);

  logInfo(
    "payment-submission",
    "POST request triggered to select-payment-method",
    { paymentMethod: PaymentMethod.CREDIT_CARD, isWalletOnly: true },
  );

  const newSession = await client.selectPaymentMethod(
    PaymentMethod.CREDIT_CARD,
    {
      termsAccepted: options.termsAccepted,
      documentsSigned: options.documentsSigned,
      shouldUseWalletBalance: true,
    },
  );

  logInfo(
    "payment-submission",
    "POST response received from select-payment-method",
    {
      sessionId: newSession.id,
      status: newSession.status,
      paymentMethod: PaymentMethod.CREDIT_CARD,
      termsAccepted: options.termsAccepted,
      documentsSigned: options.documentsSigned,
      shouldUseWalletBalance: true,
    },
  );

  emitPaymentSubmissionTracking("payment_submission_completed", session, {
    payment_method_type: "wallet_only",
    wallet_selected: true,
  });

  browserAgent.addPageAction("PAYMENT_SUBMISSION_METHOD", {
    payment_method_used: "wallet_only",
    wallet_selected: true,
  });

  return newSession;
}
